if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      constructor() {
        super();
        this.cachedMap = new Map();
        this.focusElement = this.input;
        this.searchContent = this.querySelector('.search__content');
        this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
        this.header = document.querySelector('header');
        this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

        this.isHeaderSearch = this.closest('header') !== null;

        this.states = {
          OPEN: 'predictive-search-open',
          LOADING: 'btn--loading',
          SEARCH_OPEN: 'search-open'
        };
      }

      connectedCallback() {
        // The header and the search page re-render in the theme editor: the document listeners must go with them.
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.resetButton?.addEventListener('click', this.clear.bind(this), { signal });
        this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300), { signal });
        this.input.addEventListener('focus', this.onFocus.bind(this), { signal });
        document.addEventListener('click', this.handleClickOutside.bind(this), { signal });
        this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this), { signal });
        this.form?.addEventListener('submit', this.onSubmit.bind(this), { signal });
        this.addEventListener('click', async (event) => {
          const link = event.target.closest('a[href]');
          if (!link) return;
          const url = new URL(link.href, window.location.origin);
          const action = new URL(this.form.action, window.location.origin);
          if (url.origin !== action.origin || url.pathname !== action.pathname || !url.searchParams.get('q')) return;
          const colorSearch = this.colorSectionURL(url.searchParams.get('q'));
          if (colorSearch && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
            event.preventDefault();
            try {
              const resolved = await this.resolveColorSearch(colorSearch.url.toString());
              const target = new URL(resolved.url);
              target.searchParams.delete('section_id');
              window.location.assign(target.toString());
            } catch (error) { this.showSearchError(); console.error(error); }
            return;
          }
          url.searchParams.set('q', this.buildSearchQuery(url.searchParams.get('q')));
          url.searchParams.delete('page');
          link.href = url.toString();
        }, { signal });

        document.addEventListener(
          'menu-drawer:open',
          () => {
            this.classList.remove('predictive-search-open');
            document.body.classList.remove('search-open');
          },
          { signal }
        );
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }
      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get searchProductTypes() {
        return this.querySelector('.search__types select');
      }

      get form() {
        return this.querySelector('form');
      }

      get headerCollapseOnScroll() {
        return this.header.dataset.collapseOnScroll === 'true';
      }

      onFocus(event) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom)}px`
        );
        if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(false);
        }

        if (!this.searchRecommendationEmpty) {
          this.searchContent.classList.remove('hidden');
        }
        this.classList.add('predictive-search-open');
        if (this.getQuery().length === 0) {
          if (this.searchRecommendationEmpty) {
            this.searchContent.classList.add('hidden');
          }
          return;
        }
        const url = this.setupURL().toString();
        // Reopening shows the results of a query the buyer already searched: not a search update.
        this.renderSection(url, { isReopen: true });
      }

      getQuery() {
        return this.input.value.trim();
      }

      clear(event = null) {
        event?.preventDefault();
        this.mareldRequestNumber = (this.mareldRequestNumber || 0) + 1;
        this.setLoadingState(false);
        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
        this.toggleSearchState(false);
      }

      handleProductTypeChange(evt) {
        const query = this.getQuery();
        if (query.length > 0) {
          const url = this.setupURL().toString();
          this.renderSection(url);
        }
      }

      getSearchColorGroups() {
        if (!this.mareldColorGroups) {
          this.mareldColorGroups = (this.dataset.searchColorMap || '').trim().split(';').filter(Boolean).map((row) => {
            const [canonical, aliases] = row.split('=');
            return { canonical, aliases: (aliases || '').split(',') };
          });
        }
        return this.mareldColorGroups;
      }

      // Use the shop's actual product types for ambiguous category words.
      // A Legging is not a Wide-Leg trouser; shoes also include Sneaker/Stiefel.
      getProductSearchIntent(text) {
        const intents = [
          { aliases: ['legging', 'leggings'], types: ['Leggings', 'Legging'] },
          { aliases: ['schuh', 'schuhe', 'schuhen'], types: ['Sneaker', 'Halbschuh', 'Mokassin', 'Loafer', 'Stiefelette', 'Stiefel', 'Pumps', 'Sandale', 'Espadrille', 'Ballerina', 'Clog', 'Pantolette'] },
          { aliases: ['strumpf', 'strümpfe', 'struempfe', 'strümpfen', 'struempfen'], types: ['Strumpf', 'Strumpfhose'], typeWords: ['Strumpf', 'Strumpfhose'], exactTitles: ['Strumpfhose'] }
        ];
        const words = text.split(/\s+/).filter(Boolean);
        const matched = intents.filter((intent) => words.some((word) => intent.aliases.includes(word.toLocaleLowerCase('de-DE'))));
        // Do not guess an intersection/union for two different category requests.
        if (matched.length !== 1) return null;
        const intent = matched[0];
        return { ...intent, remaining: words.filter((word) => !intent.aliases.includes(word.toLocaleLowerCase('de-DE'))) };
      }

      // MARELD: no description/tag-only matches in the product search.
      buildSearchQuery(query, productType = '') {
        const text = query.trim();
        if (!text) return '';
        // Keep explicit Shopify search syntax usable.
        const explicit = /\b(?:title|body|product_type|tag|vendor|variants\.(?:sku|barcode|title))\s*:/.test(text);
        let scoped = text;
        if (!explicit) {
          const intent = this.getProductSearchIntent(text);
          const terms = text.split(/\s+/).filter(Boolean);
          const fields = ['title', 'product_type', 'vendor', 'variants.title', 'variants.sku', 'variants.barcode'];
          scoped = terms.map((term, index) => {
            const word = term.toLocaleLowerCase('de-DE');
            if (intent?.aliases.includes(word)) {
              // Include the entered singular/plural alias as an actual type
              // synonym. The first clause also preserves the displayed wording.
              const types = [...new Set([term, ...intent.types])];
              const clauses = intent.typeWords
                ? [`product_type:"${term}"`, ...intent.typeWords.map((type) => `product_type:${type}`)]
                : types.map((type) => `product_type:"${type}"`);
              // Phrase matching also finds the full German compound word,
              // including products missed by the stemmed type/word search.
              for (const title of intent.exactTitles || []) clauses.push(`title:"${title}"`);
              return '(' + clauses.join(' OR ') + ')';
            }
            const group = this.getSearchColorGroups().find((entry) => entry.aliases.includes(word));
            const normalized = group ? group.canonical : term;
            const escapeTerm = (value) => value.replace(/([\\:"'()*])/g, '\\$1');
            const suffix = index === terms.length - 1 && term.length >= 2 ? '*' : '';
            const value = escapeTerm(normalized) + suffix;
            const clauses = fields.map((field) => `${field}:${value}`);
            // The first title clause retains the original wording for the visible search term.
            if (normalized !== term) clauses.unshift(`title:${escapeTerm(term) + suffix}`);
            return '(' + clauses.join(' OR ') + ')';
          }).join(' AND ');

        }
        if (productType && !/^product_type:/.test(text)) {
          const safeType = productType.replace(/([\\"])/g, '\\$1');
          scoped = `product_type:"${safeType}" AND ${scoped}`;
        }
        return scoped;
      }

      normalizeSearchSize(value) {
        const aliases = { xxs: '2xs', xxl: '2xl', xxxl: '3xl', xxxxl: '4xl',
          'one-size': 'onesize', einheitsgröße: 'onesize', einheitsgroesse: 'onesize', unisize: 'onesize' };
        const text = String(value || '').toLocaleLowerCase('de-DE').replace(/\s/g, '').replace(/,/g, '.').replace(/–/g, '-');
        if (aliases[text]) return aliases[text];
        return text.split(/[/-]/).map((part) => aliases[part] || part).join('/');
      }

      detectSearchSize(value) {
        const key = this.normalizeSearchSize(value);
        const parts = key.split('/');
        return parts.every((part) => /^(?:2xs|xs|s|m|l|xl|[2-6]xl|onesize|(?:[2-9]\d|1[01]\d|120)(?:\.5|[a-k])?)$/.test(part)) ? key : '';
      }

      searchSizeMatches(label, requested) {
        const value = this.normalizeSearchSize(label);
        return value === requested || value.split('/').includes(requested);
      }

      getColorSearchPlan(query, productType = '') {
        const text = query.trim();
        if (/\b(?:title|body|product_type|tag|vendor|variants\.(?:sku|barcode|title))\s*:/.test(text)) return null;
        const groups = [];
        const sizes = [];
        const remaining = [];
        for (const term of text.split(/\s+/).filter(Boolean)) {
          const group = this.getSearchColorGroups().find((entry) => entry.aliases.includes(term.toLocaleLowerCase('de-DE')));
          if (group) {
            if (!groups.some((entry) => entry.canonical === group.canonical)) groups.push(group);
          } else {
            const size = this.detectSearchSize(term);
            if (size) {
              if (!sizes.includes(size)) sizes.push(size);
            } else remaining.push(term);
          }
        }
        // One requested size, optionally with one color. Keep ambiguous searches
        // as ordinary text instead of silently choosing one size or color.
        if (groups.length > 1 || sizes.length > 1 || (!groups.length && !sizes.length)) return null;
        if (sizes.length) {
          for (let i = remaining.length - 1; i >= 0; i--) {
            if (/^(?:größe|groesse|size)$/i.test(remaining[i])) remaining.splice(i, 1);
          }
        }
        // Keep the original wording in a redundant title clause. The remaining
        // terms determine relevance; Shopify's actual facet determines the color.
        const original = text.replace(/([\\"])/g, '\\$1');
        const base = remaining.length ? this.buildSearchQuery(remaining.join(' ')) : 'title:*';
        let q = `title:"${original}" OR ${base}`;
        if (productType) {
          const type = productType.replace(/([\\"])/g, '\\$1');
          q = `product_type:"${type}" AND (${q})`;
        }
        return { groups, sizes, q };
      }

      colorSectionURL(query, productType = '') {
        const plan = this.getColorSearchPlan(query, productType);
        if (!plan) return null;
        const url = new URL(this.form.getAttribute('action'), window.location.origin);
        url.searchParams.set('q', plan.q);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.set('section_id', 'mareld-color-search');
        return { url, plan };
      }

      async fetchMarkup(url) {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Search could not be loaded');
        const html = await response.text();
        if (html.includes('Liquid error') || html.includes('Liquid syntax error')) throw new Error('Search section could not be rendered');
        return html;
      }

      async resolveColorSearch(url) {
        this.mareldResolvedColors ||= new Map();
        if (this.mareldResolvedColors.has(url)) return this.mareldResolvedColors.get(url);
        const pending = (async () => {
          const html = await this.fetchMarkup(url);
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const root = doc.querySelector('[data-mareld-color-results]');
          if (!root) throw new Error('The MARELD color search section is missing');
          const target = new URL(url);
          const original = target.searchParams.get('q').split('title:"')[1]?.split('" OR ')[0]?.replace(/\\([\\"])/g, '$1') || '';
          const plan = this.getColorSearchPlan(original);
          const values = Array.from(doc.querySelectorAll('[data-mareld-color-value]'));
          const selected = (plan?.groups || []).map((group) => values.find((node) => group.aliases.includes(node.dataset.label.toLocaleLowerCase('de-DE'))));
          const sizeValues = Array.from(doc.querySelectorAll('[data-mareld-size-value]'));
          const sizeMatches = (plan?.sizes || []).map((size) => sizeValues.filter((node) => this.searchSizeMatches(node.dataset.label, size)));
          if ((!selected.length && !sizeMatches.length) || selected.some((node) => !node) || sizeMatches.some((nodes) => !nodes.length)) {
            // If the shop has no corresponding structured color, retain the
            // text search instead of dropping the color from the buyer's query.
            target.searchParams.set('q', this.buildSearchQuery(original, this.searchProductTypes?.value || ''));
            return { url: target.toString(), html: await this.fetchMarkup(target.toString()) };
          }
          for (const node of [...selected, ...sizeMatches.flat()]) {
            if (!target.searchParams.getAll(node.dataset.param).includes(node.dataset.value)) {
              target.searchParams.append(node.dataset.param, node.dataset.value);
            }
          }
          return { url: target.toString(), html: await this.fetchMarkup(target.toString()) };
        })();
        this.mareldResolvedColors.set(url, pending);
        try { return await pending; }
        catch (error) { this.mareldResolvedColors.delete(url); throw error; }
      }

      async onSubmit(event) {
        const query = this.getQuery();
        if (!query) {
          event.preventDefault();
          this.input.focus();
          return;
        }
        event.preventDefault();
        const params = new URLSearchParams(new FormData(this.form));
        const colorSearch = this.colorSectionURL(query, this.searchProductTypes?.value || '');
        if (colorSearch) {
          this.setLoadingState(true);
          try {
            const resolved = await this.resolveColorSearch(colorSearch.url.toString());
            const target = new URL(resolved.url);
            target.searchParams.delete('section_id');
            window.location.assign(target.toString());
          } catch (error) {
            console.error(error);
            this.showSearchError();
          } finally { this.setLoadingState(false); }
          return;
        }
        params.set('q', this.buildSearchQuery(query, this.searchProductTypes?.value || ''));
        params.set('options[prefix]', 'last');
        params.delete('page');
        const url = new URL(this.form.getAttribute('action'), window.location.origin);
        url.search = params.toString();
        window.location.assign(url.toString());
      }

      setupURL() {
        const colorSearch = this.colorSectionURL(this.getQuery(), this.searchProductTypes?.value || '');
        if (colorSearch) return colorSearch.url;
        const url = new URL(FoxTheme.routes.predictive_search_url, window.shopUrl || window.location.origin);
        url.searchParams.set('q', this.buildSearchQuery(this.getQuery(), this.searchProductTypes?.value || ''));
        url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }
        const url = this.setupURL().toString();
        this.renderSection(url);
      }

      renderSection(url, { isReopen = false } = {}) {
        const searchUpdate = isReopen
          ? null
          : FoxTheme.storefrontEvents?.start(this, 'SearchUpdateEvent', {
              search: { query: this.getQuery() }
            });

        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, searchUpdate)
          : this.renderSectionFromFetch(url, searchUpdate);
      }

      renderSectionFromCache(url, searchUpdate) {
        this.mareldRequestNumber = (this.mareldRequestNumber || 0) + 1;
        this.setLoadingState(false);
        const responseText = this.cachedMap.get(url);
        (this.renderSearchResults(responseText), this.setAttribute('results', ''));
        searchUpdate?.resolve({ totalCount: this.getTotalResults() });
      }

      showSearchError() {
        this.searchContent?.classList.remove('hidden');
        this.toggleSearchState(true);
        let message = this.querySelector('[data-mareld-search-error]');
        if (!message) {
          message = document.createElement('p');
          message.dataset.mareldSearchError = '';
          message.setAttribute('role', 'status');
          this.searchContent?.prepend(message);
        }
        message.textContent = document.documentElement.lang.startsWith('de')
          ? 'Die Suche konnte gerade nicht geladen werden. Bitte erneut versuchen.'
          : 'Search could not be loaded. Please try again.';
      }

      async renderSectionFromFetch(url, searchUpdate) {
        this.setLoadingState(true);
        this.mareldRequestNumber = (this.mareldRequestNumber || 0) + 1;
        const requestNumber = this.mareldRequestNumber;
        try {
          const isColorSearch = new URL(url).searchParams.get('section_id') === 'mareld-color-search';
          const responseText = isColorSearch ? (await this.resolveColorSearch(url)).html : await this.fetchMarkup(url);
          this.cachedMap.set(url, responseText);
          if (requestNumber !== this.mareldRequestNumber) {
            searchUpdate?.resolve({ totalCount: 0 });
            return;
          }
          this.renderSearchResults(responseText);
          searchUpdate?.resolve({ totalCount: this.getTotalResults() });
        } catch (error) {
          searchUpdate?.reject(error);
          if (requestNumber === this.mareldRequestNumber) this.showSearchError();
          console.error('Error fetching search:', error);
        } finally {
          if (requestNumber === this.mareldRequestNumber) this.setLoadingState(false);
        }
      }

      getTotalResults() {
        const results = document.getElementById(`PredictiveSearchResults-${FoxTheme.utils.getSectionId(this)}`);
        return Number(results?.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
      }
      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
        const targetElement = document.getElementById(id);

        if (targetElement) {
          const parser = new DOMParser();
          const parsedDoc = parser.parseFromString(responseText, 'text/html');
          const contentElement = parsedDoc.getElementById(id) || parsedDoc.querySelector('[data-mareld-color-results]');

          if (contentElement) {
            this.searchContent?.classList.remove('hidden');
            this.querySelector('[data-mareld-search-error]')?.remove();
            targetElement.innerHTML = contentElement.innerHTML;
            targetElement.querySelectorAll('[data-mareld-view-all]').forEach((button) => button.setAttribute('form', this.form.id));

            if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
              document.body.classList.add('search-open');
              this.toggleNavButtonVisibility(true);
              this.classList.add('predictive-search-open');
            } else {
              this.classList.add('predictive-search-open');
            }
          } else {
            console.error(`Element with id '${id}' not found in the parsed response.`);
          }
        } else {
          console.error(`Element with id '${id}' not found in the document.`);
        }
      }

      handleClickOutside(event) {
        const target = event.target;
        const shouldClose = this.isHeaderSearch
          ? !this.contains(target) &&
            ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
              target.classList.contains('header__search-close'))
          : !this.contains(target);

        if (shouldClose) {
          setTimeout(() => this.toggleSearchState(false));
        }
      }

      toggleSearchState(isOpen) {
        this.classList.toggle(this.states.OPEN, isOpen);
        if (this.isHeaderSearch) {
          document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
          this.toggleNavButtonVisibility(!isOpen);
        }
        if (!isOpen && this.searchRecommendationEmpty) {
          this.searchContent?.classList.add('hidden');
        }
      }

      toggleNavButtonVisibility(isOpen) {
        this.toggleNavigationButton &&
          this.headerCollapseOnScroll &&
          this.toggleNavigationButton.classList.toggle('is-show', isOpen);
      }

      setLoadingState(isLoading) {
        if (isLoading) {
          this.setAttribute('loading', 'true');
          this.resetButton?.classList.add(this.states.LOADING);
        } else {
          this.removeAttribute('loading');
          this.resetButton?.classList.remove(this.states.LOADING);
          this.setAttribute('results', 'true');
        }
      }
    }
  );
}


// Facet updates replace the grid but leave the heading and header input in place.
// Read the server-rendered term so removing a color cannot leave a stale heading.
if (typeof document !== 'undefined') {
  const syncMareldSearchTerm = () => {
    const termNode = document.querySelector('[data-mareld-active-search-term]');
    const heading = document.querySelector('[data-mareld-search-heading-prefix]');
    if (!termNode || !heading) return;
    const term = termNode.dataset.mareldActiveSearchTerm;
    if (new URLSearchParams(window.location.search).get('type') === 'product') {
      document.querySelectorAll('form[is="facet-form"]').forEach((form) => {
        if (!form.querySelector('input[name="type"]')) {
          const input = document.createElement('input');
          input.type = 'hidden'; input.name = 'type'; input.value = 'product';
          form.append(input);
        }
      });
    }
    heading.textContent = term
      ? `${heading.dataset.mareldSearchHeadingPrefix} „${term}“`
      : heading.dataset.mareldSearchHeadingPrefix.replace(/ (für|for)$/, '');
    document.querySelectorAll('predictive-search input[type="search"]').forEach((input) => {
      if (document.activeElement !== input) input.value = term;
    });
  };
  const startMareldSearchTermSync = () => {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid || grid.dataset.mareldTermObserver) return;
    grid.dataset.mareldTermObserver = 'true';
    new MutationObserver(syncMareldSearchTerm).observe(grid, { childList: true, subtree: true });
    syncMareldSearchTerm();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startMareldSearchTermSync, { once: true });
  else startMareldSearchTermSync();
}
